// Typed client for the control-plane REST API (admin bearer token).
// Fleet management: tenants, gateway inventory with liveness, and lifecycle
// actions. No PHI or result values cross this surface.
import { errorFrom } from './index';
function authHeaders(opts) {
    return { Authorization: `Bearer ${opts.adminToken}` };
}
async function request(opts, method, path, body) {
    const f = opts.fetchImpl ?? fetch;
    const headers = { ...authHeaders(opts) };
    const init = { method, headers };
    if (body !== undefined) {
        headers['Content-Type'] = 'application/json';
        init.body = JSON.stringify(body);
    }
    const res = await f(new URL(path, opts.baseUrl), init);
    if (!res.ok) {
        throw await errorFrom(res, path);
    }
    return res;
}
async function requestJson(opts, method, path, body) {
    const res = await request(opts, method, path, body);
    return (await res.json());
}
/** All tenants (admin). */
export function listTenants(opts) {
    return requestJson(opts, 'GET', '/api/tenants');
}
/** Create a tenant (admin). */
export function createTenant(opts, name) {
    return requestJson(opts, 'POST', '/api/tenants', { name });
}
/** Deactivate a tenant (soft; stops new enrollment, retains data/audit). */
export async function deactivateTenant(opts, tenantId) {
    await request(opts, 'POST', `/api/tenants/${encodeURIComponent(tenantId)}/deactivate`);
}
/** Reactivate a previously deactivated tenant. */
export async function reactivateTenant(opts, tenantId) {
    await request(opts, 'POST', `/api/tenants/${encodeURIComponent(tenantId)}/reactivate`);
}
/** Issue a one-time bootstrap token for a tenant (admin). */
export function issueEnrollmentToken(opts, tenantId) {
    return requestJson(opts, 'POST', `/api/tenants/${encodeURIComponent(tenantId)}/enrollment-tokens`);
}
/** Gateways for a tenant, with derived liveness (tenant-scoped). */
export function listGateways(opts, tenantId) {
    return requestJson(opts, 'GET', `/api/tenants/${encodeURIComponent(tenantId)}/gateways`);
}
/** Decommission a gateway: mark inactive and revoke its credential. */
export async function decommissionGateway(opts, tenantId, gatewayId) {
    await request(opts, 'POST', `/api/tenants/${encodeURIComponent(tenantId)}/gateways/${encodeURIComponent(gatewayId)}/decommission`);
}
/** Append-only audit events for a tenant (admin). */
export function getAudit(opts, tenantId) {
    return requestJson(opts, 'GET', `/api/tenants/${encodeURIComponent(tenantId)}/audit`);
}
/** Members of a tenant (requires user-management permission). */
export function listMembers(opts, tenantId) {
    return requestJson(opts, 'GET', `/api/tenants/${encodeURIComponent(tenantId)}/members`);
}
/** Change a member's role. Owner grants/revocations require an owner actor. */
export async function changeMemberRole(opts, tenantId, userId, role) {
    await request(opts, 'POST', `/api/tenants/${encodeURIComponent(tenantId)}/members/${encodeURIComponent(userId)}/role`, { role });
}
/** Remove a member (soft: membership deactivated, history retained). */
export async function removeMember(opts, tenantId, userId) {
    await request(opts, 'POST', `/api/tenants/${encodeURIComponent(tenantId)}/members/${encodeURIComponent(userId)}/remove`);
}
/** Invite a user into a tenant with a role. Invitation creation is durable even
 * if delivery fails; the returned flag reports whether the email was accepted. */
export function inviteMember(opts, tenantId, email, role) {
    return requestJson(opts, 'POST', `/api/tenants/${encodeURIComponent(tenantId)}/invitations`, { email, role });
}
/** Pending/handled invitations for a tenant. */
export function listInvitations(opts, tenantId) {
    return requestJson(opts, 'GET', `/api/tenants/${encodeURIComponent(tenantId)}/invitations`);
}
/** Revoke a pending invitation. */
export async function revokeInvitation(opts, tenantId, invitationId) {
    await request(opts, 'POST', `/api/tenants/${encodeURIComponent(tenantId)}/invitations/${encodeURIComponent(invitationId)}/revoke`);
}
/** Read a tenant's general settings (any member). */
export function getTenantSettings(opts, tenantId) {
    return requestJson(opts, 'GET', `/api/tenants/${encodeURIComponent(tenantId)}/settings`);
}
/** Rename a tenant (owner only). */
export function renameTenant(opts, tenantId, name) {
    return requestJson(opts, 'POST', `/api/tenants/${encodeURIComponent(tenantId)}/rename`, { name });
}
//# sourceMappingURL=control-plane.js.map